import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import { captureHelpEvent } from "src/config/posthog";
import { productForRoute } from "./routeMatch";
import type { HelpEntryPoint } from "./types";

interface OpenOptions {
  entryPoint: HelpEntryPoint;
  product?: string;
  section?: string;
}

interface HelpDrawerContextValue {
  open: boolean;
  product: string;
  scrollToSection: string | null;
  openDrawer: (opts: OpenOptions) => void;
  closeDrawer: () => void;
  clearScrollToSection: () => void;
}

const HelpDrawerContext = createContext<HelpDrawerContextValue | null>(null);

export const HelpDrawerProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [product, setProduct] = useState<string>("platform");
  const [scrollToSection, setScrollToSection] = useState<string | null>(null);

  const openDrawer = useCallback(
    ({ entryPoint, product: productOverride, section }: OpenOptions) => {
      const resolvedProduct =
        productOverride ?? productForRoute(location.pathname);
      setProduct(resolvedProduct);
      setScrollToSection(section ?? null);
      setOpen(true);
      captureHelpEvent("help_panel_opened", {
        product: resolvedProduct,
        entry_point: entryPoint,
        route: location.pathname,
      });
    },
    [location.pathname],
  );

  const closeDrawer = useCallback(() => {
    setOpen(false);
    setScrollToSection(null);
  }, []);

  const clearScrollToSection = useCallback(() => setScrollToSection(null), []);

  const value = useMemo(
    () => ({
      open,
      product,
      scrollToSection,
      openDrawer,
      closeDrawer,
      clearScrollToSection,
    }),
    [
      open,
      product,
      scrollToSection,
      openDrawer,
      closeDrawer,
      clearScrollToSection,
    ],
  );

  return (
    <HelpDrawerContext.Provider value={value}>
      {children}
    </HelpDrawerContext.Provider>
  );
};

export const useHelpDrawer = (): HelpDrawerContextValue => {
  const ctx = useContext(HelpDrawerContext);
  if (!ctx) {
    throw new Error("useHelpDrawer must be used within a HelpDrawerProvider");
  }
  return ctx;
};
